import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ShieldAlert, LogOut } from 'lucide-react';

/**
 * iter 258 — Persistent top banner that renders ONLY when the current
 * session is an impersonation (super admin acting as another user). Sits
 * above all routes via App.js mount. Designed to be visually unmissable so
 * the super admin never forgets they're operating as someone else.
 */
const ImpersonationBanner = () => {
  const { user, isImpersonating, impersonationOriginalEmail, endImpersonation } = useAuth();
  const [ending, setEnding] = useState(false);

  if (!isImpersonating) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await endImpersonation();
    } catch (e) {
      setEnding(false);
      alert('Failed to end impersonation. Please try again or log out manually.');
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="w-full bg-amber-500 text-slate-900 border-b-2 border-amber-700 px-4 py-2 flex items-center justify-between gap-3 shadow-md"
      style={{ position: 'sticky', top: 0, zIndex: 9999 }}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldAlert className="h-4 w-4 flex-shrink-0" />
        <span>
          You are impersonating{' '}
          <span className="font-bold" data-testid="impersonation-target-email">
            {user?.email || user?.name || 'this user'}
          </span>
          {impersonationOriginalEmail && (
            <>
              {' '}
              · originally signed in as{' '}
              <span className="font-bold">{impersonationOriginalEmail}</span>
            </>
          )}
        </span>
      </div>
      <button
        type="button"
        onClick={handleEnd}
        disabled={ending}
        data-testid="end-impersonation-button"
        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-amber-300 px-3 py-1 rounded text-xs font-semibold transition-colors disabled:opacity-50"
      >
        <LogOut className="h-3.5 w-3.5" />
        {ending ? 'Exiting…' : 'End Impersonation'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
